import React from 'react';
import { Box, Typography, Select, MenuItem, IconButton } from '@mui/material';
import palette from 'src/theme/palette';
import { IoChevronBack, IoChevronForward } from 'react-icons/io5';

interface CustomPaginationProps {
  count: number; // Total number of items
  rowsPerPage: number; // Number of rows displayed per page
  page: number; // Current page (1-based index)
  onPageChange: (page: number) => void; // Callback when the page changes
  onRowsPerPageChange: (rowsPerPage: number) => void; // Callback when rowsPerPage changes
  rowsPerPageOptions?: number[]; // Array of options for rows per page
}

const CustomPagination: React.FC<CustomPaginationProps> = ({
  count,
  rowsPerPage,
  page,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = [8, 10, 25],
}) => {
  const totalPages = Math.ceil(count / rowsPerPage);
  const maxVisiblePages = 5; // Adjust this number to show more or fewer pages in the center section

  const handleFirstPage = () => onPageChange(1); // Adjusted for 1-based index
  const handleLastPage = () => onPageChange(totalPages); // Adjusted for 1-based index
  const handlePreviousPage = () => onPageChange(Math.max(page - 1, 1)); // Adjusted for 1-based index
  const handleNextPage = () => onPageChange(Math.min(page + 1, totalPages)); // Adjusted for 1-based index
  const handleRowsPerPageChange = (event: React.ChangeEvent<{ value: unknown }>) =>
    onRowsPerPageChange(parseInt(event.target.value as string, 10));

  // Generate the list of page numbers to display
  const getPageNumbers = () => {
    if (totalPages <= 0) return [1];
    const startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    const length = Math.max(0, endPage - startPage + 1);
    return [...Array(length).keys()].map((num) => num + startPage);
  };

  return (
    <Box display="flex" alignItems="center" justifyContent="space-between" padding="16px">
      {/* Rows per page selector */}
      <Box display="flex" alignItems="center">
        <Typography variant="body2" style={{ marginRight: '8px' }}>
          Show result:
        </Typography>
        <Select
          value={rowsPerPage}
          onChange={handleRowsPerPageChange}
          variant="outlined"
          size="small"
          style={{ minWidth: '60px' }}
        >
          {rowsPerPageOptions.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {/* Pagination controls */}
      <Box display="flex" alignItems="center">
        {/* Previous Button */}
        <IconButton onClick={handlePreviousPage} disabled={page === 1}>
          <IoChevronBack color={palette.grey[500]} />
        </IconButton>

        {/* Page Numbers */}
        {getPageNumbers().map((pageNum) => (
          <Box
            key={pageNum}
            onClick={() => onPageChange(pageNum)}
            sx={{
              cursor: 'pointer',
              margin: '0 4px',
              borderRadius: '12px',
              backgroundColor: pageNum === page ? palette.primary.lighter : 'transparent',
              color: pageNum === page ? palette.primary.light : palette.grey[500],
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '40px',
              height: '40px',
            }}
          >
            <Typography variant="subtitle1">{pageNum}</Typography>
          </Box>
        ))}

        {/* Ellipsis and Last Page Button */}
        {page < totalPages - maxVisiblePages + 1 && (
          <>
            <Typography variant="body2" style={{ margin: '0 8px' }}>
              ...
            </Typography>
            <Box
              onClick={handleLastPage}
              sx={{
                cursor: 'pointer',
                borderRadius: '12px',
                backgroundColor: page === totalPages ? palette.primary.lighter : 'transparent',
                color: page === totalPages ? palette.primary.light : palette.grey[500],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '40px',
                height: '40px',
              }}
            >
              <Typography variant="body2">{totalPages}</Typography>
            </Box>
          </>
        )}

        {/* Next Button */}
        <IconButton onClick={handleNextPage} disabled={page === totalPages}>
          <IoChevronForward color={palette.grey[500]} />
        </IconButton>
      </Box>
    </Box>
  );
};

export default CustomPagination;
